import React, { useEffect, useContext, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { UrlContext } from "../../../contexts/UrlContext";
import {
  DayPilotCalendar,
  DayPilotMonth,
  DayPilot,
} from "@daypilot/daypilot-lite-react";
import "../../../styles/CalendarRepresentation.css";
import Swal from "sweetalert2";
import ModifySlot from "./modifySlot";
import CustomDatePickerForViewProfile from "./customDatePickerForViewProfile";

export default function CalendarForTimeSlot({
  events,
  setEvents,
  setEventsChanged,
  projectID,
  expertID,
}) {
  const { API_URL } = useContext(UrlContext);
  const calendarRef = useRef(null);
  const [viewType, setViewType] = useState("Week");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEventSlot, setNewEventSlot] = useState(null);
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
  const [isNewEventModalOpen, setIsNewEventModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [event, setEvent] = useState([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  const handleTimezoneChange = (e) => {
    const newTimezone = e.value;
    setTimezone(newTimezone);
  };

  const [state, setState] = useState({
    viewType: "Week",
    timeRangeSelectedHandling: "Enabled",
    eventDeleteHandling: "Update",
    startDate: selectedDate,
    headerDateFormat: "dddd d",
    events: [],
  });

  const [monthlyState, setMonthlyState] = useState({
    startDate: formatDateObjectToDate(selectedDate),
    events: [],
  });

  const eventToString = (startTime, endTime, timezone) => {
    const selectedTimeSlot = {
      weekday: new Intl.DateTimeFormat("en-GB", { weekday: "long" }).format(
        new Date(startTime)
      ),
      day: new Intl.DateTimeFormat("en-GB", { day: "2-digit" }).format(
        new Date(startTime)
      ),
      month: new Intl.DateTimeFormat("en-GB", { month: "long" }).format(
        new Date(startTime)
      ),
      year: new Intl.DateTimeFormat("en-GB", { year: "numeric" }).format(
        new Date(startTime)
      ),
      startTime: new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(new Date(startTime)),
      endTime: new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(new Date(endTime)),
    };
    return `${selectedTimeSlot.weekday}, ${selectedTimeSlot.day} ${selectedTimeSlot.month} ${selectedTimeSlot.year} ${selectedTimeSlot.startTime} - ${selectedTimeSlot.endTime} (${timezone})`;
  };

  function generateUniqueId() {
    return uuidv4();
  }

  // Function for updating the events dynamically
  const updateCalendarWithEvents = (newEvents) => {
    const formattedEvents = newEvents.map((event) => {
      let attributes = {
        eventType: event.eventType,
      };
      if (event.eventType === "TeamsCall") {
        attributes = {
          ...attributes,
          start: new Date(event.startTime)
            .toISOString()
            .slice(0, 19)
            .replace("T", " "),
          end: new Date(event.endTime)
            .toISOString()
            .slice(0, 19)
            .replace("T", " "),
          id: event.eventID,
          text: event.inviteTitle,
          backColor: event.backColor,
          barColor: event.barColor,

          eventID: event.eventID,
          attendees: event.attendees,
          calendarUsed: event.calendarUsed,
          callType: event.callType,
          clientEventID: event.clientEventID,
          clientName: event.clientName,
          createdBy: event.createdBy,
          date: event.date,
          endTime: new Date(event.endTime)
            .toISOString()
            .slice(0, 19)
            .replace("T", " "),
          eventDuration: event.eventDuration,
          eventID: event.eventID,
          expertEventID: event.expertEventID,
          expertID: event.expertID,
          inviteTitle: event.inviteTitle,
          isAutoRecord: event.isAutoRecord,
          meetingID: event.meetingID,
          meetingTranscript: event.meetingTranscript,
          onCallDuration: event.onCallDuration,
          projectID: event.projectID,
          startTime: new Date(event.startTime)
            .toISOString()
            .slice(0, 19)
            .replace("T", " "),
        };
      } else if (event.eventType === "consultantTimeSlot") {
        attributes = {
          ...attributes,
          start: new DayPilot.Date(event.startTime),
          end: new DayPilot.Date(event.endTime),
          id: event.eventID,
          text: event.inviteTitle,
          backColor: event.backColor,
          barColor: event.barColor,

          eventID: event.eventID,
          startTime: new DayPilot.Date(event.startTime),
          endTime: new DayPilot.Date(event.endTime),
          expertID: event.expertID,
          projectID: event.projectID,
          eventID: event.eventID,
          timezone: event.timezone,
        };
      } else if (event.eventType === "expertTimeSlot") {
        attributes = {
          ...attributes,
          start: new DayPilot.Date(event.startTime),
          end: new DayPilot.Date(event.endTime),
          id: event.eventID,
          text: event.inviteTitle,
          backColor: event.backColor,
          barColor: event.barColor,

          eventID: event.eventID,
          startTime: new DayPilot.Date(event.startTime),
          endTime: new DayPilot.Date(event.endTime),
          expertID: event.expertID,
          projectID: event.projectID,
          eventID: event.eventID,
          timezone: event.timezone,
        };
      }
      return attributes;
    });

    setState((prevState) => ({
      ...prevState,
      events: formattedEvents,
    }));

    setMonthlyState((prevState) => ({
      ...prevState,
      events: formattedEvents,
    }));
  };

  useEffect(() => {
    if (events) {
      updateCalendarWithEvents(events);
    }
  }, [events]);

  const handleViewTypeChange = (type) => {
    setViewType(type);
  };

  const handleClickOnDayOnMonthlyView = (args) => {
    const newDate = args.start.value;
    handleDateChange(new Date(newDate));
    setViewType("Week");
  };

  function formatDateObjectToDate(dateObject) {
    const date = new Date(dateObject);
    const year = date.getFullYear();
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const day = ("0" + date.getDate()).slice(-2);
    const formattedDate = `${year}-${month}-${day}`;
    return formattedDate;
  }

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setState((prevState) => ({
      ...prevState,
      startDate: date,
    }));
    setMonthlyState((prevState) => ({
      ...prevState,
      startDate: formatDateObjectToDate(date),
    }));
  };

  // Function to handle event click and open modal
  const handleEventClick = (args) => {
    if (args.e.data.eventType === "consultantTimeSlot") {
      const eventID = args.e.data.eventID;
      const clickedEvent = state.events.find(
        (event) => event.eventID === eventID
      );
      if (clickedEvent) {
        setNewEventSlot(clickedEvent);
        setIsNewEventModalOpen(true);
      }
    } else {
      const eventID = args.e.data.eventID;
      const clickedEvent = state.events.find(
        (event) => event.eventID === eventID
      );
      const unqID = generateUniqueId();
      const newEvent = {
        eventID: unqID,
        expertID: expertID,
        projectID: projectID,
        timezone: clickedEvent.timezone,
        startTime: clickedEvent.startTime,
        endTime: clickedEvent.endTime,

        start: clickedEvent.startTime,
        end: clickedEvent.endTime,
        inviteTitle: "Your selected slot!",
        // text: "Your selected slot!",
        eventType: "consultantTimeSlot",
        backColor: "#E8F5E9",
        barColor: "#46923c",
      };
      setNewEventSlot(newEvent);
      addEvent(newEvent);
    }
  };

  // Function to handle time range selection and open modal for creating new event
  const handleTimeRangeSelected = (args) => {
    const unqID = generateUniqueId();
    const newEvent = {
      eventID: unqID,
      expertID: expertID,
      projectID: projectID,
      timezone: timezone,
      startTime: args.start,
      endTime: args.end,

      start: args.start,
      end: args.end,
      inviteTitle: "Your selected slot!",
      text: "Your selected slot!",
      eventType: "consultantTimeSlot",
      backColor: "#E8F5E9",
      barColor: "#46923c",
    };
    setNewEventSlot(newEvent);
    // setIsNewEventModalOpen(true);
    addEvent(newEvent);
  };

  // Function to handle event moved
  const handleEventMoved = async (args) => {
    if (args.e.data.eventType === "consultantTimeSlot") {
      const eventID = args.e.data.eventID;
      const newStart = args.e.data.start;
      const newEnd = args.e.data.end;

      const clickedEvent = state.events.find(
        (event) => event.eventID === eventID
      );
      const modifiedEvent = {
        ...clickedEvent,
        startTime: newStart,
        endTime: newEnd,
      };
      setEvents((prevEvents) =>
        prevEvents.map((event) =>
          event.eventID === eventID ? modifiedEvent : event
        )
      );
      setEventsChanged(true);
      await modifyEvent(modifiedEvent);
    } else {
      updateCalendarWithEvents(events);
      Swal.fire({
        title: "Not Allowed",
        text: "You can modify or delete only your given timeslots!",
        icon: "warning",
        confirmButtonText: "OK",
        timer: 2500,
        timerProgressBar: true,
      });
    }
  };

  // Function to handle event resized
  const handleEventResized = async (args) => {
    if (args.e.data.eventType === "consultantTimeSlot") {
      const eventID = args.e.data.eventID;
      const newStart = args.e.data.start;
      const newEnd = args.e.data.end;

      const clickedEvent = state.events.find(
        (event) => event.eventID === eventID
      );
      const modifiedEvent = {
        ...clickedEvent,
        startTime: newStart,
        endTime: newEnd,
      };
      setEvents((prevEvents) =>
        prevEvents.map((event) =>
          event.eventID === eventID ? modifiedEvent : event
        )
      );
      setEventsChanged(true);
      await modifyEvent(modifiedEvent);
    } else {
      updateCalendarWithEvents(events);
      Swal.fire({
        title: "Not Allowed",
        text: "You can modify or delete only your given timeslots!",
        icon: "warning",
        confirmButtonText: "OK",
        timer: 2500,
        timerProgressBar: true,
      });
    }
  };

  // Function to handle saving changes and closing modal
  const handleSaveNewEvent = (updatedEvent) => {
    setIsNewEventModalOpen(false);
  };

  // Function to close the modal
  const handleCloseNewEventModal = () => {
    setIsNewEventModalOpen(false);
  };

  // Api for saving timeslots
  const modifyEvent = async (selectedEvent) => {
    if (selectedEvent.eventType === "consultantTimeSlot") {
      const passingValue = {
        ...selectedEvent,
        startTime: selectedEvent.startTime.value,
        endTime: selectedEvent.endTime.value,
      };
      const requestBody = {
        event: passingValue,
      };
      try {
        let modifiedUrl = "modify-meeting-time-slot";
        const response = await fetch(`${API_URL}${modifiedUrl}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        const responseData = await response.json();
        if (responseData.status === "success") {
          console.log("slot successfully modified!");
        } else {
          console.error("Failed to add selected slot");
        }
      } catch (error) {
        console.error("Error while calling the backend API:", error);
      }
    } else {
      updateCalendarWithEvents(events);
      Swal.fire({
        title: "Not Allowed",
        text: "You can modify or delete only your given timeslots!",
        icon: "warning",
        confirmButtonText: "OK",
        timer: 2500,
        timerProgressBar: true,
      });
    }
  };

  // API for remove expert from selected list
  const deleteTimeSlot = async (args) => {
    if (args.e.data.eventType === "consultantTimeSlot") {
      Swal.fire({
        title: "Delete this time slot?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Delete!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          setEvents((prevEvents) =>
            prevEvents.filter((event) => event.eventID !== args.e.data.eventID)
          );
          const requestBody = {
            eventID: args.e.data.eventID,
          };

          try {
            let modifiedUrl = "delete-meeting-time-slot";
            const response = await fetch(`${API_URL}${modifiedUrl}`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(requestBody),
            });

            const responseData = await response.json();
            if (responseData.status === "success") {
              Swal.fire({
                title: "Delected!",
                text: "Selected time slot has been deleted",
                icon: "success",
                timer: 2500,
                timerProgressBar: true,
              });
              setEventsChanged(true);
            } else {
              console.error("Failed to delete the slot");
              Swal.fire({
                title: "Error!",
                text: responseData.text,
                icon: "error",
                confirmButtonText: "OK",
              });
            }
          } catch (error) {
            console.error("Error while calling the backend API:", error);
            Swal.fire({
              title: "Error!",
              text: "Unexpected error!",
              icon: "error",
              confirmButtonText: "OK",
            });
          }
        } else {
          updateCalendarWithEvents(events);
        }
      });
    } else {
      updateCalendarWithEvents(events);
      Swal.fire({
        title: "Not Allowed",
        text: "You can modify or delete only your given timeslots!",
        icon: "warning",
        confirmButtonText: "OK",
        timer: 2500,
        timerProgressBar: true,
      });
    }
  };

  // Api for saving timeslots
  const addEvent = async (selectedEvent) => {
    const passingValue = {
      ...selectedEvent,
      startTime: selectedEvent.startTime.value,
      endTime: selectedEvent.endTime.value,
    };
    const requestBody = {
      event: passingValue,
    };
    try {
      let modifiedUrl = "save-meeting-time-slot";
      const response = await fetch(`${API_URL}${modifiedUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();
      if (responseData.status === "success") {
        setEvents((prevEvents) => [...prevEvents, selectedEvent]);
        setEventsChanged(true);
      } else {
        console.error("Failed to add selected slot");
      }
    } catch (error) {
      console.error("Error while calling the backend API:", error);
    }
  };

  return (
    <div>
      <div className="toolbar">
        <CustomDatePickerForViewProfile
          events={events}
          selectedDate={selectedDate}
          handelOnDayClicking={handleDateChange}
          handleViewTypeChange={handleViewTypeChange}
          timezone={timezone}
          handleTimezoneChange={handleTimezoneChange}
        />
      </div>

      {viewType === "Week" ? (
        <DayPilotCalendar
          {...state}
          ref={calendarRef}
          onTimeRangeSelected={handleTimeRangeSelected}
          onEventClick={handleEventClick}
          onEventMoved={handleEventMoved}
          onEventResized={handleEventResized}
          onEventDelete={deleteTimeSlot}
        />
      ) : (
        <DayPilotMonth
          {...monthlyState}
          ref={calendarRef}
          onEventClick={handleEventClick}
          onTimeRangeSelected={handleClickOnDayOnMonthlyView}
          onEventDelete={deleteTimeSlot}
        />
      )}

      {isNewEventModalOpen && (
        <>
          <ModifySlot
            eventData={newEventSlot}
            setEvents={setEvents}
            setEventsChanged={setEventsChanged}
            onClose={handleCloseNewEventModal}
          />
        </>
      )}
    </div>
  );
}
